import React, { useState, useEffect } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Webhook, 
  Key, 
  Plus, 
  Trash2, 
  RefreshCw, 
  Copy, 
  Check,
  AlertCircle,
  CheckCircle,
  Clock,
  Send,
  Eye,
  ExternalLink
} from 'lucide-react';
import { api } from '../../lib/api';

const APIIntegrationPage = () => {
  const [webhooks, setWebhooks] = useState([]);
  const [apiKeys, setApiKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deliveries, setDeliveries] = useState([]);
  const [selectedWebhook, setSelectedWebhook] = useState(null);
  
  // Modals
  const [showCreateWebhook, setShowCreateWebhook] = useState(false);
  const [showCreateApiKey, setShowCreateApiKey] = useState(false);
  const [showDeliveries, setShowDeliveries] = useState(false);
  const [showNewKeyModal, setShowNewKeyModal] = useState(false);
  const [newApiKey, setNewApiKey] = useState('');
  
  // Form states
  const [newWebhookUrl, setNewWebhookUrl] = useState('');
  const [newKeyName, setNewKeyName] = useState('');
  const [copiedKey, setCopiedKey] = useState(false);

  useEffect(() => {
    fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [webhooksRes, keysRes] = await Promise.all([
        api.get('/admin/webhooks'),
        api.get('/admin/api-keys')
      ]);
      
      if (webhooksRes.ok) {
        const data = await webhooksRes.json();
        setWebhooks(data.webhooks || []);
      }
      
      if (keysRes.ok) {
        const data = await keysRes.json();
        setApiKeys(data.api_keys || []);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const createWebhook = async () => {
    if (!newWebhookUrl) {
      toast.error('URL requise');
      return;
    }
    
    try {
      const response = await api.post('/admin/webhooks', {
        url: newWebhookUrl,
        events: ['lead.created']
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success('Webhook créé');
        toast.info(`Secret: ${data.secret}`, { duration: 10000 });
        setNewWebhookUrl('');
        setShowCreateWebhook(false);
        fetchData();
      } else {
        toast.error('Erreur lors de la création');
      }
    } catch (error) {
      console.error('Error creating webhook:', error);
      toast.error('Erreur lors de la création');
    }
  };

  const toggleWebhook = async (webhookId, currentStatus) => {
    try {
      const response = await api.put(`/admin/webhooks/${webhookId}?is_active=${!currentStatus}`);
      
      if (response.ok) {
        toast.success(currentStatus ? 'Webhook désactivé' : 'Webhook activé');
        fetchData();
      }
    } catch (error) {
      console.error('Error toggling webhook:', error);
      toast.error('Erreur');
    }
  };

  const deleteWebhook = async (webhookId) => {
    if (!confirm('Supprimer ce webhook ?')) return;
    
    try {
      const response = await api.delete(`/admin/webhooks/${webhookId}`);
      
      if (response.ok) {
        toast.success('Webhook supprimé');
        fetchData();
      }
    } catch (error) {
      console.error('Error deleting webhook:', error);
      toast.error('Erreur');
    }
  };

  const testWebhook = async (webhookId) => {
    try {
      const response = await api.post(`/admin/webhooks/${webhookId}/test`);
      
      if (response.ok) {
        toast.success('Test envoyé ! Vérifiez les logs.');
        // Refresh deliveries if viewing
        if (selectedWebhook?.id === webhookId) {
          fetchDeliveries(webhookId);
        }
      }
    } catch (error) {
      console.error('Error testing webhook:', error);
      toast.error('Erreur');
    }
  };

  const fetchDeliveries = async (webhookId) => {
    try {
      const response = await api.get(`/admin/webhooks/${webhookId}/deliveries`);
      
      if (response.ok) {
        const data = await response.json();
        setDeliveries(data.deliveries || []);
      }
    } catch (error) {
      console.error('Error fetching deliveries:', error);
    }
  };

  const viewDeliveries = (webhook) => {
    setSelectedWebhook(webhook);
    fetchDeliveries(webhook.id);
    setShowDeliveries(true);
  };

  const createApiKey = async () => {
    if (!newKeyName) {
      toast.error('Nom requis');
      return;
    }
    
    try {
      const response = await api.post(`/admin/api-keys?name=${encodeURIComponent(newKeyName)}`);
      
      if (response.ok) {
        const data = await response.json();
        setNewApiKey(data.api_key);
        setShowCreateApiKey(false);
        setShowNewKeyModal(true);
        setNewKeyName('');
        fetchData();
      } else {
        toast.error('Erreur lors de la création');
      }
    } catch (error) {
      console.error('Error creating API key:', error);
      toast.error('Erreur lors de la création');
    }
  };

  const toggleApiKey = async (keyId, currentStatus) => {
    try {
      const response = await api.put(`/admin/api-keys/${keyId}?is_active=${!currentStatus}`);
      
      if (response.ok) {
        toast.success(currentStatus ? 'Clé désactivée' : 'Clé activée');
        fetchData();
      }
    } catch (error) {
      console.error('Error toggling API key:', error);
      toast.error('Erreur');
    }
  };

  const deleteApiKey = async (keyId) => {
    if (!confirm('Supprimer cette clé API ?')) return;
    
    try {
      const response = await api.delete(`/admin/api-keys/${keyId}`);
      
      if (response.ok) {
        toast.success('Clé API supprimée');
        fetchData();
      }
    } catch (error) {
      console.error('Error deleting API key:', error);
      toast.error('Erreur');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(true);
    setTimeout(() => setCopiedKey(false), 2000);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'success':
        return <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200"><CheckCircle className="h-3 w-3 mr-1" />Succès</Badge>;
      case 'failed':
        return <Badge className="bg-red-50 text-red-700 border-red-200"><AlertCircle className="h-3 w-3 mr-1" />Échec</Badge>;
      case 'retrying':
        return <Badge className="bg-amber-50 text-amber-700 border-amber-200"><Clock className="h-3 w-3 mr-1" />Retry</Badge>;
      default:
        return <Badge className="bg-gray-50 text-gray-600 border-gray-200"><Clock className="h-3 w-3 mr-1" />En attente</Badge>;
    }
  };

  return (
    <div className="p-6" data-testid="api-integration-page">
      <div className="mb-6">
        <h1 className="text-2xl font-display font-semibold text-[#111827]">Intégration API</h1>
        <p className="text-muted-foreground text-sm mt-1">
          Connectez votre CRM pour recevoir les leads en temps réel
        </p>
      </div>

      <Tabs defaultValue="webhooks" className="space-y-6">
        <TabsList>
          <TabsTrigger value="webhooks" className="gap-2">
            <Webhook className="h-4 w-4" />
            Webhooks
          </TabsTrigger>
          <TabsTrigger value="api-keys" className="gap-2">
            <Key className="h-4 w-4" />
            Clés API
          </TabsTrigger>
          <TabsTrigger value="documentation" className="gap-2">
            <ExternalLink className="h-4 w-4" />
            Documentation
          </TabsTrigger>
        </TabsList>

        {/* Webhooks Tab */}
        <TabsContent value="webhooks" className="space-y-4">
          <div className="flex justify-between items-center">
            <p className="text-sm text-muted-foreground">
              Recevez les nouveaux leads instantanément sur votre CRM
            </p>
            <Button onClick={() => setShowCreateWebhook(true)} className="gap-2">
              <Plus className="h-4 w-4" />
              Ajouter un webhook
            </Button>
          </div>

          <Card>
            {webhooks.length === 0 ? (
              <div className="p-8 text-center">
                <Webhook className="h-12 w-12 text-muted-foreground/50 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-[#111827] mb-2">Aucun webhook configuré</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Configurez un webhook pour recevoir les leads en temps réel
                </p>
                <Button onClick={() => setShowCreateWebhook(true)}>
                  Configurer un webhook
                </Button>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>URL</TableHead>
                    <TableHead>Secret</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead>Créé le</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {webhooks.map((webhook) => (
                    <TableRow key={webhook.id}>
                      <TableCell className="font-mono text-sm max-w-[300px] truncate">
                        {webhook.url}
                      </TableCell>
                      <TableCell className="font-mono text-xs text-muted-foreground">
                        {webhook.secret_preview}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={webhook.is_active}
                            onCheckedChange={() => toggleWebhook(webhook.id, webhook.is_active)}
                          />
                          <span className="text-sm">{webhook.is_active ? 'Actif' : 'Inactif'}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {formatDate(webhook.created_at)}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => testWebhook(webhook.id)}
                            title="Envoyer un test"
                          >
                            <Send className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => viewDeliveries(webhook)}
                            title="Voir les logs"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => deleteWebhook(webhook.id)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Card>
        </TabsContent>

        {/* API Keys Tab */}
        <TabsContent value="api-keys" className="space-y-4">
          <div className="flex justify-between items-center">
            <p className="text-sm text-muted-foreground">
              Utilisez une clé API pour récupérer les leads depuis votre CRM
            </p>
            <Button onClick={() => setShowCreateApiKey(true)} className="gap-2">
              <Plus className="h-4 w-4" />
              Créer une clé API
            </Button>
          </div>

          <Card>
            {apiKeys.length === 0 ? (
              <div className="p-8 text-center">
                <Key className="h-12 w-12 text-muted-foreground/50 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-[#111827] mb-2">Aucune clé API</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Créez une clé API pour accéder aux données depuis votre CRM
                </p>
                <Button onClick={() => setShowCreateApiKey(true)}>
                  Créer une clé API
                </Button>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nom</TableHead>
                    <TableHead>Préfixe</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead>Dernière utilisation</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {apiKeys.map((key) => (
                    <TableRow key={key.id}>
                      <TableCell className="font-medium">{key.name}</TableCell>
                      <TableCell className="font-mono text-sm text-muted-foreground">
                        {key.key_prefix}...
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={key.is_active}
                            onCheckedChange={() => toggleApiKey(key.id, key.is_active)}
                          />
                          <span className="text-sm">{key.is_active ? 'Active' : 'Inactive'}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {key.last_used_at ? formatDate(key.last_used_at) : 'Jamais'}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => deleteApiKey(key.id)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Card>
        </TabsContent>

        {/* Documentation Tab */}
        <TabsContent value="documentation" className="space-y-6">
          <Card className="p-6">
            <h3 className="text-lg font-display font-semibold mb-4">API Pull - Récupérer les leads</h3>
            
            <div className="space-y-4">
              <div className="bg-slate-900 rounded-lg p-4 overflow-x-auto">
                <code className="text-sm text-slate-100">
                  <span className="text-emerald-400">GET</span> /api/external/leads?api_key=YOUR_API_KEY
                </code>
              </div>
              
              <div>
                <h4 className="font-medium mb-2">Paramètres de filtrage</h4>
                <ul className="text-sm space-y-1 text-muted-foreground">
                  <li><code className="bg-slate-100 px-1 rounded">since</code> - Date ISO (ex: 2025-01-01T00:00:00Z)</li>
                  <li><code className="bg-slate-100 px-1 rounded">status</code> - new, contacted, qualified, converted, lost</li>
                  <li><code className="bg-slate-100 px-1 rounded">requested_role</code> - Type de profil recherché</li>
                  <li><code className="bg-slate-100 px-1 rounded">limit</code> - Nombre max (défaut: 100, max: 500)</li>
                  <li><code className="bg-slate-100 px-1 rounded">offset</code> - Pagination</li>
                </ul>
              </div>

              <div>
                <h4 className="font-medium mb-2">Réponse</h4>
                <div className="bg-slate-900 rounded-lg p-4 overflow-x-auto">
                  <pre className="text-sm text-slate-100">{`{
  "success": true,
  "data": [
    {
      "lead_id": "abc123",
      "created_at": "2025-03-15T10:30:00Z",
      "name": "Jean Dupont",
      "company": "Startup Inc",
      "email": "jean@startup.com",
      "phone": "+33 6 00 00 00 00",
      "requested_role": "Assistant virtuel",
      "message": "Je recherche...",
      "source": "website",
      "status": "new"
    }
  ],
  "pagination": {
    "total": 42,
    "limit": 100,
    "offset": 0,
    "has_more": false
  }
}`}</pre>
                </div>
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <h3 className="text-lg font-display font-semibold mb-4">Webhook Push - Recevoir les leads en temps réel</h3>
            
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Votre endpoint recevra une requête POST à chaque nouveau lead :
              </p>
              
              <div>
                <h4 className="font-medium mb-2">Headers</h4>
                <ul className="text-sm space-y-1 text-muted-foreground">
                  <li><code className="bg-slate-100 px-1 rounded">X-Webhook-Signature</code> - Signature HMAC-SHA256</li>
                  <li><code className="bg-slate-100 px-1 rounded">X-Webhook-Event</code> - Type d'événement</li>
                  <li><code className="bg-slate-100 px-1 rounded">X-Webhook-Delivery-Id</code> - ID unique de livraison</li>
                </ul>
              </div>

              <div>
                <h4 className="font-medium mb-2">Payload</h4>
                <div className="bg-slate-900 rounded-lg p-4 overflow-x-auto">
                  <pre className="text-sm text-slate-100">{`{
  "event": "lead.created",
  "timestamp": "2025-03-15T10:30:00Z",
  "data": {
    "lead_id": "abc123",
    "created_at": "2025-03-15T10:30:00Z",
    "name": "Jean Dupont",
    "company": "Startup Inc",
    "email": "jean@startup.com",
    "phone": "+33 6 00 00 00 00",
    "requested_role": "Assistant virtuel",
    "message": "Je recherche...",
    "source": "website",
    "status": "new"
  }
}`}</pre>
                </div>
              </div>

              <div>
                <h4 className="font-medium mb-2">Vérification de signature</h4>
                <div className="bg-slate-900 rounded-lg p-4 overflow-x-auto">
                  <pre className="text-sm text-slate-100">{`import hmac
import hashlib
import json

def verify_signature(payload, signature, secret):
    expected = hmac.new(
        secret.encode(),
        json.dumps(payload, sort_keys=True).encode(),
        hashlib.sha256
    ).hexdigest()
    return hmac.compare_digest(f"sha256={expected}", signature)`}</pre>
                </div>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <h4 className="font-medium text-amber-800 mb-1">Retry automatique</h4>
                <p className="text-sm text-amber-700">
                  En cas d'échec (status != 2xx), 3 tentatives avec backoff exponentiel : 1min, 5min, 15min
                </p>
              </div>
            </div>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create Webhook Modal */}
      <Dialog open={showCreateWebhook} onOpenChange={setShowCreateWebhook}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ajouter un webhook</DialogTitle>
            <DialogDescription>
              Entrez l'URL de votre endpoint CRM
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="webhook-url">URL du webhook</Label>
              <Input
                id="webhook-url"
                value={newWebhookUrl}
                onChange={(e) => setNewWebhookUrl(e.target.value)}
                placeholder="https://votre-crm.com/webhooks/leads"
                className="mt-1.5"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateWebhook(false)}>
              Annuler
            </Button>
            <Button onClick={createWebhook}>
              Créer le webhook
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create API Key Modal */}
      <Dialog open={showCreateApiKey} onOpenChange={setShowCreateApiKey}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Créer une clé API</DialogTitle>
            <DialogDescription>
              Donnez un nom à cette clé pour l'identifier
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="key-name">Nom de la clé</Label>
              <Input
                id="key-name"
                value={newKeyName}
                onChange={(e) => setNewKeyName(e.target.value)}
                placeholder="Mon CRM"
                className="mt-1.5"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateApiKey(false)}>
              Annuler
            </Button>
            <Button onClick={createApiKey}>
              Créer la clé
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New API Key Modal */}
      <Dialog open={showNewKeyModal} onOpenChange={setShowNewKeyModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Clé API créée</DialogTitle>
            <DialogDescription>
              Copiez cette clé maintenant. Elle ne sera plus affichée.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="bg-slate-100 rounded-lg p-4 flex items-center justify-between gap-4">
              <code className="text-sm font-mono break-all">{newApiKey}</code>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => copyToClipboard(newApiKey)}
              >
                {copiedKey ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>
            <p className="text-sm text-amber-600 mt-3">
              ⚠️ Conservez cette clé en lieu sûr. Elle ne sera plus affichée.
            </p>
          </div>
          <DialogFooter>
            <Button onClick={() => setShowNewKeyModal(false)}>
              J'ai copié la clé
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Deliveries Modal */}
      <Dialog open={showDeliveries} onOpenChange={setShowDeliveries}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Logs de livraison</DialogTitle>
            <DialogDescription>
              {selectedWebhook?.url}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 max-h-[400px] overflow-y-auto">
            {deliveries.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">
                Aucune livraison enregistrée
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Événement</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead>Tentatives</TableHead>
                    <TableHead>Erreur</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {deliveries.map((delivery) => (
                    <TableRow key={delivery.id}>
                      <TableCell className="text-sm">
                        {formatDate(delivery.created_at)}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{delivery.event}</Badge>
                      </TableCell>
                      <TableCell>
                        {getStatusBadge(delivery.status)}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {delivery.attempts}/{delivery.max_attempts}
                      </TableCell>
                      <TableCell className="text-sm text-red-600 max-w-[200px] truncate">
                        {delivery.error_message || '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => fetchDeliveries(selectedWebhook?.id)}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Actualiser
            </Button>
            <Button onClick={() => setShowDeliveries(false)}>
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Export both as default and named export for use in Settings
export default APIIntegrationPage;
export { APIIntegrationPage };
